import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionGuidance } from '@/types/optibay';

const sb = supabase as any;

export function useSessionGuidance(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-guidance', sessionId],
    queryFn: async (): Promise<OptiBaySessionGuidance[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_guidance')
        .select('*')
        .eq('session_id', sessionId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionGuidance[];
    },
    enabled: !!sessionId,
  });
}

export interface AddGuidanceParams {
  sessionId: string;
  sessionTestPathId?: string;
  guidanceType?: string;
  title: string;
  body: string;
  evidenceGateMet?: boolean;
  sortOrder?: number;
}

export function useAddGuidance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddGuidanceParams) => {
      const { data, error } = await sb
        .from('optibay_session_guidance')
        .insert({
          session_id: params.sessionId,
          session_test_path_id: params.sessionTestPathId || null,
          guidance_type: params.guidanceType || 'pattern_hint',
          title: params.title,
          body: params.body,
          evidence_gate_met: params.evidenceGateMet ?? false,
          sort_order: params.sortOrder ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionGuidance;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-guidance', data.session_id] });
    },
  });
}

export function useRemoveGuidance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      const { error } = await sb
        .from('optibay_session_guidance')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-guidance', result.sessionId] });
    },
  });
}
